import React, {useEffect, useState} from 'react';
import {App, Button, Empty, Modal, Select, Table} from 'antd';
import {useI18n} from '../../../i18n/useI18n';
import {backtestDcaPlan, getDcaUserConfig} from '../../../services/dcaService';
import type {DcaBacktestResult, DcaBacktestTrigger} from '../../../types/dca';
import {fmtTime} from './dcaConditionUtil';

/** 按周期分桶的回测范围选项（与后端上限一致：≤5m 7天 / 15m·30m 30天 / 1H+ 90天） */
const rangeOptionsFor = (timeframe: string): number[] => {
    if ('1m' === timeframe || '3m' === timeframe || '5m' === timeframe) {
        return [7];
    }
    if ('15m' === timeframe || '30m' === timeframe) {
        return [7, 30];
    }
    return [7, 30, 90];
};

/** 数值格式化（null/undefined → --；去掉多余尾零） */
const fmtNum = (v?: number | null): string => null == v ? '--' : String(Number(v.toFixed(8)));

interface Props {
    open: boolean;
    /** 测算条件组合 JSON（表单当前值或计划已存值） */
    conditionsJson: string;
    symbol: string;
    timeframe: string;
    lifecycle: 'ONCE' | 'LOOP';
    maxBuyCount: number;
    onClose: () => void;
}

/**
 * 计划测算弹窗（P2，F-19 条件回测）
 * 范围选择（按周期分桶）→ 后端回放历史K线（与实盘共用条件引擎）→
 * 结果统计卡（触发次数/累计买入/平均成本/最新价/浮盈亏/最大回撤）+ 触发明细表。
 */
const DcaBacktestModal: React.FC<Props> = ({
                                               open, conditionsJson, symbol, timeframe, lifecycle, maxBuyCount, onClose,
                                           }) => {
    const {t} = useI18n();
    const {message} = App.useApp();
    const [rangeDays, setRangeDays] = useState<number>(90);
    const [amountUsdt, setAmountUsdt] = useState<number | null>(null);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<DcaBacktestResult | null>(null);

    // 打开时：范围重置为当前周期可选项最大值、清空旧结果、拉用户配置金额做提示
    useEffect(() => {
        if (!open) {
            return;
        }
        const options = rangeOptionsFor(timeframe);
        setRangeDays(options[options.length - 1]);
        setResult(null);
        setAmountUsdt(null);
        void getDcaUserConfig()
            .then((cfg) => setAmountUsdt(cfg?.amountUsdt ?? null))
            .catch(() => setAmountUsdt(null));
    }, [open, timeframe]);

    const run = async () => {
        setRunning(true);
        try {
            setResult(await backtestDcaPlan({
                conditionsJson, symbol, timeframe, rangeDays, lifecycle, maxBuyCount,
            }));
        } catch (e: any) {
            message.error(e?.message ?? t('dca.backtest.error'));
            setResult(null);
        } finally {
            setRunning(false);
        }
    };

    const pnl = result?.unrealizedPnl;
    const pnlClass = null == pnl ? '' : (pnl >= 0 ? 'dca-cost-pos' : 'dca-cost-neg');

    const columns = [
        {
            title: t('dca.backtest.list.time'),
            dataIndex: 'triggerTime',
            width: 170,
            render: (v: number) => fmtTime(v),
        },
        {
            title: t('dca.backtest.list.price'),
            dataIndex: 'price',
            width: 130,
            render: (v: number) => fmtNum(v),
        },
        {
            title: t('dca.backtest.list.amount'),
            dataIndex: 'amountUsdt',
            width: 110,
            render: (v: number) => fmtNum(v),
        },
        {
            title: t('dca.backtest.list.quantity'),
            dataIndex: 'quantity',
            render: (v: number) => fmtNum(v),
        },
    ];

    return (
        <Modal
            open={open}
            title={t('dca.backtest.title')}
            width={680}
            onCancel={onClose}
            footer={null}
            maskClosable={false}
        >
            <div className="dca-backtest">
                <div className="dca-backtest-controls">
                    <span className="dca-form-label">{t('dca.backtest.rangeDays')}</span>
                    <Select
                        style={{width: 150}}
                        value={rangeDays}
                        options={rangeOptionsFor(timeframe).map((d) => ({
                            label: t(`dca.backtest.range.${d}`),
                            value: d,
                        }))}
                        onChange={(v) => setRangeDays(v)}
                    />
                    <Button type="primary" loading={running} onClick={() => void run()}>
                        {t('dca.backtest.run')}
                    </Button>
                </div>
                {null != amountUsdt && amountUsdt > 0 ? (
                    <div className="dca-backtest-tip">
                        {t('dca.backtest.amountTip').replace('{amount}', String(amountUsdt))}
                    </div>
                ) : null}

                {null != result ? (
                    <>
                        <div className="dca-cost">
                            <div className="dca-cost-item">
                                <span className="dca-cost-label">{t('dca.backtest.stats.triggers')}</span>
                                <span>{result.totalTriggers}</span>
                            </div>
                            <div className="dca-cost-item">
                                <span className="dca-cost-label">{t('dca.backtest.stats.quantity')}</span>
                                <span>{fmtNum(result.totalBuyQuantity)}</span>
                            </div>
                            <div className="dca-cost-item">
                                <span className="dca-cost-label">{t('dca.backtest.stats.amount')}</span>
                                <span>{fmtNum(result.totalBuyAmount)}</span>
                            </div>
                            <div className="dca-cost-item">
                                <span className="dca-cost-label">{t('dca.backtest.stats.avgCost')}</span>
                                <span>{fmtNum(result.avgCost)}</span>
                            </div>
                            <div className="dca-cost-item">
                                <span className="dca-cost-label">{t('dca.backtest.stats.latestPrice')}</span>
                                <span>{fmtNum(result.latestPrice)}</span>
                            </div>
                            <div className="dca-cost-item">
                                <span className="dca-cost-label">{t('dca.backtest.stats.pnl')}</span>
                                <span className={pnlClass}>
                                    {null == pnl ? '--' : `${pnl >= 0 ? '+' : ''}${fmtNum(pnl)}`}
                                </span>
                            </div>
                            <div className="dca-cost-item">
                                <span className="dca-cost-label">{t('dca.backtest.stats.maxDrawdown')}</span>
                                <span>{fmtNum(result.maxPriceDrawdownPct)}%</span>
                            </div>
                        </div>
                        <div className="dca-backtest-meta">
                            {t('dca.backtest.rangeInfo')}: {fmtTime(result.startTime)} ~ {fmtTime(result.endTime)}
                            {' · '}{t('dca.backtest.klineCount')} {result.klineCount}
                        </div>
                        {0 === result.totalTriggers ? (
                            <Empty description={t('dca.backtest.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                        ) : (
                            <Table<DcaBacktestTrigger>
                                rowKey="triggerTime"
                                size="small"
                                columns={columns}
                                dataSource={result.triggers}
                                pagination={{pageSize: 10, hideOnSinglePage: true}}
                            />
                        )}
                    </>
                ) : null}
            </div>
        </Modal>
    );
};

export default DcaBacktestModal;
